import { useEffect, useRef, useState } from 'react'

import checkCircleSuccessIcon from '@/assets/icons/check-circle-success.svg'
import LinkIcon from '@/assets/icons/link.svg?react'

import { Container, IconBadge, MessageLine, TextGroup, TimestampLine } from './styles'
import type { CompletionToastVariant } from './utils'

const TOAST_VISIBLE_DURATION_MS = 3000
const EXIT_ANIMATION_DURATION_MS = 300

const SHARE_BADGE_BG_COLOR = '#3b82f6'
const CHECK_CIRCLE_ICON_SIZE_PX = 24

const MESSAGE: Record<CompletionToastVariant, string> = {
  share: '링크가 복사되었어요',
  alarm: '광고성 알림 수신에 동의했어요',
}

interface CompletionToastProps {
  variant: CompletionToastVariant | null
  onDismiss: () => void
}

const formatTimestamp = (date: Date) => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`
}

export default function CompletionToast({ variant, onDismiss }: CompletionToastProps) {
  const [isExiting, setIsExiting] = useState(false)
  const [timestamp, setTimestamp] = useState<string | null>(() => (variant ? formatTimestamp(new Date()) : null))
  const [trackedVariant, setTrackedVariant] = useState(variant)
  const onDismissRef = useRef(onDismiss)

  // variant 변경 시 reset — 렌더 단계 setState 패턴 (useEffect + setState 안티패턴 회피)
  if (variant !== trackedVariant) {
    setTrackedVariant(variant)
    setIsExiting(false)
    setTimestamp(variant ? formatTimestamp(new Date()) : null)
  }

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    if (!variant) return

    const visibleTimer = setTimeout(() => {
      setIsExiting(true)
    }, TOAST_VISIBLE_DURATION_MS)

    const dismissTimer = setTimeout(() => {
      onDismissRef.current()
    }, TOAST_VISIBLE_DURATION_MS + EXIT_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(visibleTimer)
      clearTimeout(dismissTimer)
    }
  }, [variant])

  if (!variant) return null

  const message = MESSAGE[variant]

  return (
    <Container isExiting={isExiting}>
      {variant === 'alarm' ? (
        <img
          src={checkCircleSuccessIcon}
          alt=''
          width={CHECK_CIRCLE_ICON_SIZE_PX}
          height={CHECK_CIRCLE_ICON_SIZE_PX}
        />
      ) : (
        <IconBadge bgColor={SHARE_BADGE_BG_COLOR}>
          <LinkIcon />
        </IconBadge>
      )}
      <TextGroup>
        <MessageLine>{message}</MessageLine>
        {variant === 'alarm' && timestamp && <TimestampLine>{timestamp}</TimestampLine>}
      </TextGroup>
    </Container>
  )
}
